'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { nextPrayerFrom } from '../../lib/prayer-next'
import { formatIndianNumber } from '../../lib/format-number'

/* ─────────────────────────────────────────────────────────────
   Live ticker — sits ABOVE the navbar at the very top of the
   page (approved design position). Rendered by the root layout,
   homepage-only for now. Self-contained data: /api/prayer +
   /api/stats (both server-cached).
───────────────────────────────────────────────────────────── */

type TickerStats = { mapped: number; cities: number; verified: number }

export default function HomeTicker({ initialStats }: { initialStats?: TickerStats | null }) {
  const pathname = usePathname()
  const [prayerLine, setPrayerLine] = useState<string | null>(null)
  /* SSR-fed from the layout — first paint already shows engine numbers */
  const [stats, setStats] = useState<TickerStats | null>(initialStats || null)

  const onHome = pathname === '/'

  useEffect(() => {
    if (!onHome) return
    let alive = true
    // NOTE: city is HARDCODED to 'Hyderabad' (the pilot base city) — the prayer-adhan
    // line and the "verified · Hyderabad" scope label are NOT user-location-aware yet.
    // Real numbers (mapped/verified/pledged) come from /api/stats below; only the city
    // scope is fixed. Wire to the user's location when geo is available app-wide.
    fetch('/api/prayer?city=Hyderabad')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d?.Fajr) return
        const np = nextPrayerFrom(d)
        if (np) setPrayerLine(`${np.name.charAt(0)}${np.name.slice(1).toLowerCase()} adhan at ${np.time}`)
      })
      .catch(() => {})
    fetch('/api/stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.mapped) setStats(d) })
      .catch(() => {})
    return () => { alive = false }
  }, [onHome])

  const items = useMemo(() => [
    <span key="t1"><em>●</em> {prayerLine ? <>{prayerLine} — <b>Hyderabad</b></> : <>Calculated prayer times — <b>Hyderabad</b></>}</span>,
    ...(stats ? [
      <span key="t2"><i>★</i> <b>{formatIndianNumber(stats.mapped)} masjids</b> mapped across <b>{stats.cities} cities</b> — live now</span>,
      // Honesty: verified is a NATIONAL count — no hardcoded city suffix. The "₹ pledged across
      // N campaigns" item was removed — the platform has processed no donations, so a money
      // figure would advertise a capability that does not exist.
      <span key="t3"><em>◆</em> <b>{stats.verified} verified masjids</b> — across India</span>,
    ] : []),
    <span key="t5"><em>▤</em> <b>MasjidOS</b> — committee tools live for founding masjids</span>,
    <span key="t6"><i>⬡</i> <b>Waqf Guardian</b> — register and map your masjid&rsquo;s waqf properties</span>,
  ], [prayerLine, stats])

  if (!onHome) return null

  return (
    <div className="hticker" role="marquee" aria-label="Live platform updates">
      <div className="ht-label"><span className="ht-dot" />LIVE</div>
      <div className="ht-track">
        {items}
        {items.map((el, i) => React.cloneElement(el, { key: 'dup' + i }))}
      </div>

      <style jsx>{`
        .hticker{background:linear-gradient(90deg,#083D32,#0A5C49);border-bottom:1px solid rgba(255,255,255,.08);overflow:hidden;height:34px;display:flex;align-items:center}
        .ht-label{flex:none;display:flex;align-items:center;gap:8px;padding:0 15px;height:100%;background:rgba(255,255,255,.08);border-right:1px solid rgba(255,255,255,.1);font-family:var(--f-mono);font-size:10px;letter-spacing:.14em;color:rgba(255,255,255,.85)}
        .ht-dot{width:6px;height:6px;border-radius:50%;background:#14B8A6;box-shadow:0 0 8px #14B8A6;animation:htpulse 1.6s infinite}
        @keyframes htpulse{0%,100%{opacity:1}50%{opacity:.35}}
        .ht-track{display:flex;gap:46px;white-space:nowrap;animation:htscroll 42s linear infinite;padding-left:22px}
        .ht-track :global(span){font-family:var(--f-mono);font-size:11px;color:rgba(255,255,255,.85)}
        .ht-track :global(b){color:#FFFFFF;font-weight:600}
        .ht-track :global(i){font-style:normal;color:#E6C963}
        .ht-track :global(em){font-style:normal;color:#7DEFE0}
        @keyframes htscroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        @media(prefers-reduced-motion:reduce){.ht-track{animation:none}.ht-dot{animation:none}}
      `}</style>
    </div>
  )
}
